import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import Logo from '../components/Logo';
import UpgradeScreen from '../components/UpgradeScreen';
import { hasFullAccess } from '../lib/platform';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Home,
  Calculator,
  GraduationCap,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  Calendar,
  Zap,
  Check,
  ExternalLink,
  Trash2,
  TestTube,
  Loader2,
  Link as LinkIcon,
  ChevronLeft,
  Plug,
  Clock,
  RefreshCw,
  Mail,
  MessageSquare,
  FileSpreadsheet,
  Bell,
  TrendingUp,
  Search,
  BookOpen,
  Copy,
  ArrowRight
} from 'lucide-react';

const IntegrationsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const isClubMember = hasFullAccess(user?.subscription_tier);
  
  // Calendly state
  const [calendlyConfig, setCalendlyConfig] = useState({ configured: false, calendly_url: '' });
  const [calendlyUrl, setCalendlyUrl] = useState('');
  const [calendlyLoading, setCalendlyLoading] = useState(false);
  
  // Zapier state
  const [zapierConfig, setZapierConfig] = useState({ configured: false, webhook_url: '', trigger_events: [] });
  const [zapierUrl, setZapierUrl] = useState('');
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [zapierLoading, setZapierLoading] = useState(false);
  const [testingZapier, setTestingZapier] = useState(false);

  useEffect(() => {
    if (isClubMember) {
      fetchCalendlyConfig();
      fetchZapierConfig();
      fetchAvailableEvents();
    }
  }, [isClubMember]);

  // Show upgrade screen for non-club members
  if (!isClubMember) {
    return (
      <UpgradeScreen
        icon={Plug}
        iconBgClass="from-orange-500 to-amber-600"
        iconColorClass="text-orange-500"
        buttonGradientClass="from-orange-500 to-amber-600"
        title="Intégrations"
        currentTier={user?.subscription_tier}
        features={[
          { icon: Calendar, title: "Calendly", description: "Planifiez automatiquement vos RDV avec des investisseurs" },
          { icon: Zap, title: "Zapier", description: "Automatisez vos workflows avec +5000 applications" },
          { icon: RefreshCw, title: "Automatisations", description: "Gagnez du temps avec des processus automatisés" }
        ]}
      />
    );
  }

  // ================== CALENDLY ==================

  const fetchCalendlyConfig = async () => {
    try {
      const response = await axios.get(`${API}/integrations/calendly/config`, { withCredentials: true });
      setCalendlyConfig(response.data);
      if (response.data.calendly_url) {
        setCalendlyUrl(response.data.calendly_url);
      }
    } catch (error) {
      console.error('Failed to fetch Calendly config:', error);
    }
  };

  const saveCalendlyConfig = async () => {
    if (!calendlyUrl) {
      toast.error('Veuillez entrer votre URL Calendly');
      return;
    }
    
    setCalendlyLoading(true);
    try {
      await axios.post(`${API}/integrations/calendly/config`, {
        calendly_url: calendlyUrl,
        event_types: []
      }, { withCredentials: true });
      
      toast.success('Configuration Calendly enregistrée');
      fetchCalendlyConfig();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setCalendlyLoading(false);
    }
  };

  const deleteCalendlyConfig = async () => {
    try {
      await axios.delete(`${API}/integrations/calendly/config`, { withCredentials: true });
      setCalendlyConfig({ configured: false, calendly_url: '' });
      setCalendlyUrl('');
      toast.success('Configuration Calendly supprimée');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // ================== ZAPIER ==================

  const fetchZapierConfig = async () => {
    try {
      const response = await axios.get(`${API}/integrations/zapier/config`, { withCredentials: true });
      setZapierConfig(response.data);
      if (response.data.webhook_url) {
        setZapierUrl(response.data.webhook_url);
      }
      if (response.data.trigger_events) {
        setSelectedEvents(response.data.trigger_events);
      }
    } catch (error) {
      console.error('Failed to fetch Zapier config:', error);
    }
  };

  const fetchAvailableEvents = async () => {
    try {
      const response = await axios.get(`${API}/integrations/events`, { withCredentials: true });
      setAvailableEvents(response.data.events || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const saveZapierConfig = async () => {
    if (!zapierUrl) {
      toast.error('Veuillez entrer votre URL webhook Zapier');
      return;
    }
    
    if (!zapierUrl.startsWith('https://hooks.zapier.com/')) {
      toast.error('L\'URL doit commencer par https://hooks.zapier.com/');
      return;
    }
    
    setZapierLoading(true);
    try {
      await axios.post(`${API}/integrations/zapier/config`, {
        webhook_url: zapierUrl,
        trigger_events: selectedEvents
      }, { withCredentials: true });
      
      toast.success('Configuration Zapier enregistrée');
      fetchZapierConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setZapierLoading(false);
    }
  };

  const deleteZapierConfig = async () => {
    try {
      await axios.delete(`${API}/integrations/zapier/config`, { withCredentials: true });
      setZapierConfig({ configured: false, webhook_url: '', trigger_events: [] });
      setZapierUrl('');
      setSelectedEvents([]);
      toast.success('Configuration Zapier supprimée');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const testZapierWebhook = async () => {
    setTestingZapier(true);
    try {
      await axios.post(`${API}/integrations/zapier/test`, {}, { withCredentials: true });
      toast.success('Test réussi ! Vérifiez votre Zap');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Échec du test');
    } finally {
      setTestingZapier(false);
    }
  };

  const toggleEvent = (eventId) => {
    setSelectedEvents(prev => 
      prev.includes(eventId) 
        ? prev.filter(e => e !== eventId)
        : [...prev, eventId]
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-slate-600 hover:text-[#0D47A1] hover:bg-[#0D47A1]/5">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Dashboard
                </Button>
              </Link>
              <Link to="/dashboard" className="hidden md:flex">
                <img src="/logo-horizontal.png" alt="10 Millions Investor" className="h-16 sm:h-20 w-auto" data-testid="header-logo" />
              </Link>
              <nav className="hidden lg:flex space-x-1 ml-4 pl-4 border-l border-slate-200">
                <Link to="/calculator" className="px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors flex items-center text-sm">
                  <Calculator className="w-4 h-4 mr-2" /> Calculateur
                </Link>
                <Link to="/integrations" className="px-3 py-2 rounded-lg bg-[#0D47A1]/10 text-[#0D47A1] flex items-center text-sm font-medium">
                  <Settings className="w-4 h-4 mr-2" /> Intégrations
                </Link>
              </nav>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/profile" className="hidden md:flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#0D47A1] flex items-center justify-center text-white text-sm font-medium">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                )}
              </Link>
              <Button variant="ghost" size="sm" onClick={logout} className="hidden md:flex text-slate-600 hover:text-red-600">
                <LogOut className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 py-4 px-4 space-y-2">
            <Link to="/dashboard" className="block px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100">Tableau de bord</Link>
            <Link to="/calculator" className="block px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100">Calculateur</Link>
            <Link to="/integrations" className="block px-4 py-2 rounded-lg bg-[#0D47A1]/10 text-[#0D47A1] font-medium">Intégrations</Link>
            <Link to="/profile" className="block px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100">Mon Profil</Link>
            <button onClick={logout} className="w-full text-left px-4 py-2 rounded-lg text-red-600 hover:bg-red-50">Déconnexion</button>
          </div>
        )}
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-r from-[#0D47A1] to-[#1565C0] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-4">
            <Settings className="w-12 h-12" />
            <div>
              <h1 className="text-3xl font-bold">Intégrations</h1>
              <p className="text-blue-100">Connectez vos outils préférés à 10M Investor</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* Calendly Integration */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="flex items-center gap-2">
                    Calendly
                    {calendlyConfig.configured && (
                      <Badge className="bg-green-100 text-green-700">
                        <Check className="w-3 h-3 mr-1" /> Connecté
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription>Permettez à vos clients de prendre rendez-vous directement</CardDescription>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="calendly-url">URL de votre page Calendly</Label>
              <div className="flex gap-2">
                <Input
                  id="calendly-url"
                  placeholder="https://calendly.com/votre-nom"
                  value={calendlyUrl}
                  onChange={(e) => setCalendlyUrl(e.target.value)}
                />
                <Button onClick={saveCalendlyConfig} disabled={calendlyLoading}>
                  {calendlyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
                </Button>
              </div>
              <p className="text-sm text-slate-500">
                Copiez l'URL de votre page de prise de rendez-vous Calendly.{' '}
                <a href="https://calendly.com" target="_blank" rel="noopener noreferrer" className="text-[#0D47A1] hover:underline">
                  Créer un compte Calendly <ExternalLink className="w-3 h-3 inline" />
                </a>
              </p>
            </div>
            
            {calendlyConfig.configured && (
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Votre widget de prise de rendez-vous</p>
                    <p className="text-xs text-slate-500">Visible sur votre profil public</p>
                  </div>
                  <Button variant="destructive" size="sm" onClick={deleteCalendlyConfig}>
                    <Trash2 className="w-4 h-4 mr-1" /> Supprimer
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Zapier Integration */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <CardTitle className="flex items-center gap-2">
                    Zapier
                    {zapierConfig.configured && (
                      <Badge className="bg-green-100 text-green-700">
                        <Check className="w-3 h-3 mr-1" /> Connecté
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription>Automatisez vos workflows avec plus de 5000 applications</CardDescription>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="zapier-url">URL de votre Webhook Zapier</Label>
              <div className="flex gap-2">
                <Input
                  id="zapier-url"
                  placeholder="https://hooks.zapier.com/hooks/catch/..."
                  value={zapierUrl}
                  onChange={(e) => setZapierUrl(e.target.value)}
                />
              </div>
              <p className="text-sm text-slate-500">
                Créez un Zap avec le trigger "Webhooks by Zapier" → "Catch Hook".{' '}
                <a href="https://zapier.com/apps/webhook/integrations" target="_blank" rel="noopener noreferrer" className="text-[#0D47A1] hover:underline">
                  Guide Zapier <ExternalLink className="w-3 h-3 inline" />
                </a>
              </p>
            </div>

            {/* Event triggers */}
            <div className="space-y-3">
              <Label>Événements déclencheurs</Label>
              <p className="text-sm text-slate-500">Sélectionnez les événements qui déclencheront votre Zap</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {availableEvents.map((event) => (
                  <div
                    key={event.id}
                    className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedEvents.includes(event.id) ? 'border-[#0D47A1] bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => toggleEvent(event.id)}
                  >
                    <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                      selectedEvents.includes(event.id) ? 'bg-[#0D47A1] border-[#0D47A1]' : 'border-slate-300'
                    }`}>
                      {selectedEvents.includes(event.id) && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{event.name}</p>
                      <p className="text-xs text-slate-500 truncate">{event.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={saveZapierConfig} disabled={zapierLoading}>
                {zapierLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Enregistrer
              </Button>
              {zapierConfig.configured && (
                <>
                  <Button variant="outline" onClick={testZapierWebhook} disabled={testingZapier}>
                    {testingZapier ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <TestTube className="w-4 h-4 mr-2" />}
                    Tester
                  </Button>
                  <Button variant="destructive" onClick={deleteZapierConfig}>
                    <Trash2 className="w-4 h-4 mr-1" /> Supprimer
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Zapier Templates Section */}
        <Card className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50">
          <CardHeader>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  Templates Zapier
                  <Badge className="bg-orange-500 text-white">Nouveau</Badge>
                </CardTitle>
                <CardDescription>Des automatisations prêtes à l'emploi pour booster votre productivité</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Template 1: Email pour annonces rentables */}
            <div className="bg-white rounded-xl p-5 border border-orange-100 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Email pour annonces rentables</h4>
                    <p className="text-sm text-slate-600 mt-1">Recevez un email quand l'Agent IA trouve une annonce avec un rendement &gt;7%</p>
                    <div className="flex items-center gap-2 mt-3">
                      <Badge variant="outline" className="text-xs">Recherche IA</Badge>
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                      <Badge variant="outline" className="text-xs">Gmail / Outlook</Badge>
                    </div>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText("Événement: ai_search_complete\nCondition: estimated_yield > 7\nAction: Envoyer email avec titre, prix, rendement, URL");
                    toast.success("Configuration copiée !");
                  }}
                >
                  <Copy className="w-4 h-4 mr-1" /> Copier
                </Button>
              </div>
              <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                <p className="text-xs font-medium text-slate-700 mb-2">Configuration :</p>
                <ol className="text-xs text-slate-600 space-y-1 list-decimal list-inside">
                  <li>Créez un Zap avec le trigger "Webhooks by Zapier" → "Catch Hook"</li>
                  <li>Copiez l'URL webhook et collez-la ci-dessus</li>
                  <li>Sélectionnez l'événement <span className="font-mono bg-slate-200 px-1 rounded">Recherche IA terminée</span></li>
                  <li>Ajoutez un filtre : <span className="font-mono bg-slate-200 px-1 rounded">best_match_yield</span> supérieur à 7</li>
                  <li>Action : Gmail → Envoyer un email avec les données de l'annonce</li>
                </ol>
              </div>
            </div>

            {/* Template 2: SMS pour gros deals */}
            <div className="bg-white rounded-xl p-5 border border-orange-100 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">SMS pour projets MDB importants</h4>
                    <p className="text-sm text-slate-600 mt-1">Recevez un SMS quand vous créez un projet MDB avec un profit &gt;50 000€</p>
                    <div className="flex items-center gap-2 mt-3">
                      <Badge variant="outline" className="text-xs">Projet MDB</Badge>
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                      <Badge variant="outline" className="text-xs">Twilio SMS</Badge>
                    </div>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText("Événement: new_mdb_project\nCondition: profit_estime > 50000\nAction: Envoyer SMS avec nom projet, profit, rentabilité");
                    toast.success("Configuration copiée !");
                  }}
                >
                  <Copy className="w-4 h-4 mr-1" /> Copier
                </Button>
              </div>
              <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                <p className="text-xs font-medium text-slate-700 mb-2">Configuration :</p>
                <ol className="text-xs text-slate-600 space-y-1 list-decimal list-inside">
                  <li>Trigger : "Webhooks by Zapier" → "Catch Hook"</li>
                  <li>Sélectionnez l'événement <span className="font-mono bg-slate-200 px-1 rounded">Nouveau projet MDB</span></li>
                  <li>Ajoutez un filtre : <span className="font-mono bg-slate-200 px-1 rounded">profit_estime</span> supérieur à 50000</li>
                  <li>Action : Twilio → Envoyer SMS : "🏗️ Projet: [nom] - Profit: [profit]€"</li>
                </ol>
              </div>
            </div>

            {/* Template 3: Google Sheets pour calculs */}
            <div className="bg-white rounded-xl p-5 border border-orange-100 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Export vers Google Sheets</h4>
                    <p className="text-sm text-slate-600 mt-1">Ajoutez automatiquement chaque calcul de rentabilité dans un tableau</p>
                    <div className="flex items-center gap-2 mt-3">
                      <Badge variant="outline" className="text-xs">Nouveau calcul</Badge>
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                      <Badge variant="outline" className="text-xs">Google Sheets</Badge>
                    </div>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText("Événement: new_calculation\nAction: Ajouter ligne avec nom, prix_achat, rendement_brut, rendement_net, cash_flow, régime");
                    toast.success("Configuration copiée !");
                  }}
                >
                  <Copy className="w-4 h-4 mr-1" /> Copier
                </Button>
              </div>
              <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                <p className="text-xs font-medium text-slate-700 mb-2">Configuration :</p>
                <ol className="text-xs text-slate-600 space-y-1 list-decimal list-inside">
                  <li>Trigger : "Webhooks by Zapier" → "Catch Hook"</li>
                  <li>Sélectionnez l'événement <span className="font-mono bg-slate-200 px-1 rounded">Nouveau calcul de rentabilité</span></li>
                  <li>Action : Google Sheets → Créer une ligne de feuille de calcul</li>
                  <li>Mappez les champs : nom, prix_achat, rendement_brut, rendement_net, cash_flow_mensuel, regime_recommande</li>
                </ol>
              </div>
            </div>

            {/* Template 4: Slack pour formations */}
            <div className="bg-white rounded-xl p-5 border border-orange-100 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Notification Slack - Cours terminé</h4>
                    <p className="text-sm text-slate-600 mt-1">Célébrez chaque cours terminé avec une notification dans votre channel</p>
                    <div className="flex items-center gap-2 mt-3">
                      <Badge variant="outline" className="text-xs">Cours terminé</Badge>
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                      <Badge variant="outline" className="text-xs">Slack</Badge>
                    </div>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText("Événement: course_completed\nAction: Message Slack: 🎉 Formation terminée: [course_title] - Durée: [duration] min");
                    toast.success("Configuration copiée !");
                  }}
                >
                  <Copy className="w-4 h-4 mr-1" /> Copier
                </Button>
              </div>
              <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                <p className="text-xs font-medium text-slate-700 mb-2">Configuration :</p>
                <ol className="text-xs text-slate-600 space-y-1 list-decimal list-inside">
                  <li>Trigger : "Webhooks by Zapier" → "Catch Hook"</li>
                  <li>Sélectionnez l'événement <span className="font-mono bg-slate-200 px-1 rounded">Cours terminé</span></li>
                  <li>Action : Slack → Envoyer un message dans un canal</li>
                  <li>Message : "🎉 Formation terminée : [course_title] | Catégorie : [category]"</li>
                </ol>
              </div>
            </div>

            {/* Help Link */}
            <div className="flex items-center justify-center pt-4">
              <a 
                href="https://zapier.com/apps/webhook/integrations" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-orange-600 hover:text-orange-700 font-medium"
              >
                <ExternalLink className="w-4 h-4" />
                Guide complet Zapier Webhooks
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Coming Soon Integrations */}
        <Card className="opacity-60">
          <CardHeader>
            <CardTitle>Prochainement</CardTitle>
            <CardDescription>D'autres intégrations sont en cours de développement</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { name: 'Notion', icon: '📝' },
                { name: 'Slack', icon: '💬' },
                { name: 'Google Sheets', icon: '📊' },
                { name: 'Airtable', icon: '📋' }
              ].map((integration) => (
                <div key={integration.name} className="text-center p-4 bg-slate-50 rounded-lg">
                  <div className="text-3xl mb-2">{integration.icon}</div>
                  <p className="text-sm font-medium text-slate-600">{integration.name}</p>
                  <Badge variant="outline" className="mt-2">Bientôt</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default IntegrationsPage;
